import { z } from 'zod';
/**
 * Canonical event types the engine emits. Free-form at the DB level
 * (`event.type: string`), but consumers should match these.
 */
export const EventTypeSchema = z.enum([
    'run.started',
    'run.completed',
    'run.failed',
    'run.cancelled',
    'run.paused',
    'node.started',
    'node.completed',
    'node.failed',
    'node.skipped',
    /**
     * One emitted per message produced by a node during execution — LLM agent
     * system/user/assistant turns, tool calls and their results. Drives the
     * triage run-detail message thread (DEV-16173).
     */
    'node.message',
    'tool.invoked',
    'tool.failed',
    'hitl.requested',
    'hitl.resolved',
    /**
     * Emitted when a running workflow concludes "this is real" and opens an
     * Signal via the `open_signal` tool (DEV-16227). The Signal
     * Orchestrator (DEV-16226) subscribes to this to start watching the new
     * signal for duplicate runs.
     */
    'signal.opened',
    /**
     * Emitted by the Signal Orchestrator (DEV-16226) when it decides a run
     * is a duplicate of an open signal (Mode A) or an in-flight run sharing
     * the same key (Mode B). Carries the decision *and* whether the orchestrator
     * actually enforced it (`enforced: true` = run was cancelled; `false` =
     * dry-run observation, no cancellation). One event per decided run.
     */
    'run.deduplicated',
    /**
     * Emitted when an Signal transitions to RESOLVED (HITL clears it).
     * Consumers (Orchestrator, Inbox FE) drop it from active tracking.
     */
    'signal.resolved',
    /**
     * Emitted when an Signal's `assignedTo` changes (assign or clear).
     * Webhook consumers + the live view subscribe to this without re-querying.
     */
    'signal.assigned',
    /**
     * Emitted by `EvaluationService.runEvaluation` when an Evaluation
     * transitions to COMPLETED — i.e. every Run in the batch finished and
     * `scoreSummary` is populated. Parent-grained so consumers (notably the
     * theopolis refinement-trigger dispatcher) fire once per batch, not once
     * per child Run.
     */
    'evaluation.completed',
    /**
     * Emitted when a persistent-agent thread raises an elicitation via its
     * `request_human_input` tool (DEV-16877) — the PA analog of
     * `hitl.requested`. Run-less: `runId` is null; the payload carries `orgId`
     * + `persistentAgentId` + `elicitationId`, and the message rides the
     * org channel (`cortex:org:{orgId}`) so webhooks can match without a run.
     */
    'elicitation.requested',
    /**
     * Emitted when an elicitation leaves PENDING — answered by a human,
     * cancelled by an operator, or expired by the sweep. The PA analog of
     * `hitl.resolved`; payload carries `outcome: answered|cancelled|expired`.
     */
    'elicitation.resolved',
]);
export const EventSchema = z.object({
    id: z.string(),
    runId: z.string().nullable(),
    type: z.string(),
    payload: z.record(z.string(), z.unknown()),
    createdAt: z.date(),
});
/**
 * One message in a node-execution's conversation. Mirrors the OpenAI-ish
 * chat-message shape across providers — every LLM SDK we use already
 * speaks something compatible. Stored in `NodeExecution.trace.messages`
 * AND emitted as `node.message` events for live SSE consumers. (DEV-16173.)
 */
// `reasoning` carries the model's hidden-chain-of-thought stream (Mastra
// emits these as `reasoning-*` chunks). They're not part of the OpenAI
// chat-message vocabulary but every recent Anthropic/OpenAI model surfaces
// them, and platform-app's task thread renders them as a distinct kind so
// users can audit how a tool call was reached. Adding the role here keeps
// trace.messages a single ordered list instead of a parallel reasoning
// channel.
export const NodeMessageRoleSchema = z.enum(['system', 'user', 'assistant', 'tool', 'reasoning']);
export const NodeMessageSchema = z.object({
    /**
     * Stable per-message id within a node execution. Lets UI consumers
     * dedupe when the same logical message is emitted multiple times
     * (e.g. an assistant turn that accumulates streamed text-deltas and
     * is re-emitted with growing content). Optional — when omitted,
     * consumers fall back to `createdAt` for ordering.
     */
    id: z.string().optional(),
    role: NodeMessageRoleSchema,
    /** Free-form text body — markdown rendered in the UI. May be empty on tool-only messages. */
    content: z.string(),
    /** Optional sender label — agent name for assistant, tool name for tool. */
    name: z.string().optional(),
    /** When `role === 'tool'`, the `toolCalls[i].id` this message resolves. */
    toolCallId: z.string().optional(),
    /**
     * Authoritative failure flag on a `role === 'tool'` result, stamped by the
     * llm-agent stream processor when the runtime emits a `tool-error` chunk
     * (or the result envelope carries `isError`). Only ever written as `true` —
     * ABSENCE does not mean success: MCP tools routinely report failures as
     * ordinary results whose text says "Error in …", which never becomes a
     * `tool-error` chunk. Readers must fall back to inspecting the body when
     * the flag is absent.
     */
    isError: z.boolean().optional(),
    /** Tool calls issued by an assistant message (in OpenAI's shape). */
    toolCalls: z
        .array(z.object({
        id: z.string(),
        name: z.string(),
        args: z.record(z.string(), z.unknown()).optional(),
    }))
        .optional(),
    /** ISO timestamp; defaults to the moment of emission. */
    createdAt: z.string(),
});
/**
 * Payload of a `node.message` event. The `nodeKey` + `nodeExecutionId`
 * let UI consumers group messages by node execution without re-fetching.
 */
export const NodeMessageEventPayloadSchema = z.object({
    nodeKey: z.string(),
    nodeExecutionId: z.string(),
    message: NodeMessageSchema,
});
/**
 * Payload of an `signal.opened` event (DEV-16227). Carries enough for the
 * Signal Orchestrator to start tracking without re-querying: the new
 * signal's id, its dedup `key`, and the originating run. `deduplicated`
 * is true when the call matched an already-open signal for the key (the
 * re-entrant no-op path) rather than creating a fresh one.
 */
export const SignalOpenedEventPayloadSchema = z.object({
    signalId: z.string(),
    signalKey: z.string(),
    originRunId: z.string(),
    deduplicated: z.boolean(),
});
/**
 * Payload of a `run.deduplicated` event (DEV-16226 + L2/L3 follow-on).
 * `mode` distinguishes Mode A (matched an OPEN signal) from Mode B
 * (matched an in-flight run with the same key, no signal yet).
 * `layer` reports which engine made the call — L1 deterministic, L2
 * semantic, L3 LLM-agent. `confidence` is the matcher's similarity /
 * confidence (set by L2/L3; null for L1 + all Mode B).
 */
export const RunDeduplicatedEventPayloadSchema = z.object({
    runId: z.string(),
    /** Signal the run was linked to (Mode A); null in Mode B. */
    signalId: z.string().nullable(),
    /** The other in-flight run the duplicate was linked to (Mode B); null in Mode A. */
    cancelledByRunId: z.string().nullable(),
    signalKey: z.string(),
    mode: z.enum(['A', 'B']),
    /** Which engine made the call. */
    layer: z.enum(['L1', 'L2', 'L3']),
    /** Similarity (L2 cosine, 0..1) or LLM confidence (L3, 0..1). Null otherwise. */
    confidence: z.number().min(0).max(1).nullable(),
    /** Human/LLM-readable explanation of the match. */
    reason: z.string(),
    /** True when the orchestrator cancelled the run; false in dry-run mode. */
    enforced: z.boolean(),
});
/**
 * Payload of an `signal.resolved` event (DEV-16226). Mirrors the
 * lifecycle column writes; subscribers use this to drop the signal from
 * any active-watch projection.
 */
export const SignalResolvedEventPayloadSchema = z.object({
    signalId: z.string(),
    signalKey: z.string(),
    resolvedBy: z.string().nullable(),
    resolvedReason: z.string().nullable(),
});
//# sourceMappingURL=event.js.map